import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DndContext, DragEndEvent, useDraggable, useDroppable } from '@dnd-kit/core';
import TopNavBar from '../../components/TopNavBar/TopNavBar';
import styles from './BioticIndex.module.css';
import kick1 from '../../assets/biotic-index/kicknet_1.jpg';
import kick2 from '../../assets/biotic-index/kicknet_2.jpg';
import kick3 from '../../assets/biotic-index/kicknet_3.jpg';
import kick4 from '../../assets/biotic-index/kicknet_4.jpg';
import leaves from '../../assets/biotic-index/leaves.png';
import leaves2 from '../../assets/biotic-index/leaves2.png';
import rock11 from '../../assets/biotic-index/streamrock11.png';
import rock22 from '../../assets/biotic-index/streamrock22.png';
import rock44 from '../../assets/biotic-index/streamrock44.png';
import stonefly from '../../assets/biotic-index/stonefly.png';
import dobsonfly from '../../assets/biotic-index/dobsonfly.png';
import caddisflySmall from '../../assets/biotic-index/tinycaddisfly.png';
import tinycaddisfly from '../../assets/biotic-index/tinycaddisfly.png';
import dragonfly from '../../assets/biotic-index/dragonfly.png';
import waterpenny from '../../assets/biotic-index/waterpenny.png';
import crawfish from '../../assets/biotic-index/crawfish.png';
import cranefly from '../../assets/biotic-index/cranefly.png';
import damselfly from '../../assets/biotic-index/damselfly.png';
import mayfly from '../../assets/biotic-index/mayfly.png';
import orbsnail from '../../assets/biotic-index/orbsnail.png';
import scud from '../../assets/biotic-index/scud.png';
import sowbug from '../../assets/biotic-index/sowbug.png';
import leech from '../../assets/biotic-index/leech.png';
import worm from '../../assets/biotic-index/worm.png';
import pouchsnail from '../../assets/biotic-index/pouchsnail.png';
import tinystonefly from '../../assets/biotic-index/tinystonefly.png';
import tinydobsonfly from '../../assets/biotic-index/tinydobsonfly.png';
import tinydragonfly from '../../assets/biotic-index/tinydragonfly.png';
import tinywaterpenny from '../../assets/biotic-index/tinywaterpenny.png';
import tinycrawfish from '../../assets/biotic-index/tinycrawfish.png';
import tinycranefly from '../../assets/biotic-index/tinycranefly.png';
import tinydamselfly from '../../assets/biotic-index/tinydamselfly.png';
import tinymayfly from '../../assets/biotic-index/tinymayfly.png';
import tinyorbsnail from '../../assets/biotic-index/tinyorbsnail.png';
import tinyscud from '../../assets/biotic-index/tinyscud.png';
import tinysowbug from '../../assets/biotic-index/tinysowbug.png';
import tinyleech from '../../assets/biotic-index/tinyleech.png';
import tinyworm from '../../assets/biotic-index/tinyworm.png';
import tinypouchsnail from '../../assets/biotic-index/tinypouchsnail.png';

type Macro = {
  id: string;
  name: string;
  group: 1 | 2 | 3 | 4;
  image: string;
};

type DraggableItem = {
  id: string;
  x: number;
  y: number;
  z: number;
  image: string;
  type: 'macro' | 'cover';
  macro?: Macro;
};

const macroPool: Macro[] = [
  { id: 'stonefly', name: 'Stonefly', group: 1, image: stonefly },
  { id: 'dobsonfly', name: 'Dobsonfly', group: 1, image: dobsonfly },
  { id: 'caddisfly', name: 'Caddisfly', group: 2, image: caddisflySmall },
  { id: 'dragonfly', name: 'Dragonfly', group: 2, image: dragonfly },
  { id: 'waterpenny', name: 'Waterpenny', group: 2, image: waterpenny },
  { id: 'crawfish', name: 'Crawfish', group: 2, image: crawfish },
  { id: 'cranefly', name: 'Cranefly', group: 2, image: cranefly },
  { id: 'damselfly', name: 'Damselfly', group: 2, image: damselfly },
  { id: 'mayfly', name: 'Mayfly', group: 2, image: mayfly },
  { id: 'orbsnail', name: 'Orb Snail', group: 3, image: orbsnail },
  { id: 'scud', name: 'Scud', group: 3, image: scud },
  { id: 'sowbug', name: 'Sowbug', group: 4, image: sowbug },
  { id: 'leech', name: 'Leech', group: 4, image: leech },
  { id: 'worm', name: 'Tubifex worm', group: 4, image: worm },
  { id: 'pouchsnail', name: 'Pouch Snail', group: 4, image: pouchsnail },
];

const covers = [leaves, leaves2, leaves2, rock11, rock11, rock22, rock44, rock44, leaves2];

const DraggableSprite: React.FC<{ item: DraggableItem }> = ({ item }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
  });

  const base = {
    left: item.x,
    top: item.y,
    zIndex: item.z + (isDragging ? 10 : 0),
  };

  const style = transform
    ? {
        ...base,
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : base;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      className={`${styles.draggable} ${item.type === 'cover' ? styles.cover : ''}`}
      style={style}
    >
      <img src={item.image} alt={item.macro?.name || 'cover'} />
    </div>
  );
};

const DroppableArea: React.FC<{
  id: string;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
  forwardRef?: React.MutableRefObject<HTMLDivElement | null>;
}> = ({ id, className, style, children, forwardRef }) => {
  const { setNodeRef } = useDroppable({ id });
  const refCallback = (node: HTMLDivElement | null) => {
    setNodeRef(node);
    if (forwardRef) {
      forwardRef.current = node;
    }
  };
  return (
    <div ref={refCallback} className={className} style={style}>
      {children}
    </div>
  );
};

const groupWeights: Record<1 | 2 | 3 | 4, number> = { 1: 4, 2: 3, 3: 2, 4: 1 };
const groupColors: Record<1 | 2 | 3 | 4, string> = {
  1: '#4db3ff',
  2: '#6ecf68',
  3: '#f0a03c',
  4: '#f46868',
};

const BioticIndex: React.FC = () => {
  const [activeMacros, setActiveMacros] = useState<Macro[]>([]);
  const [items, setItems] = useState<DraggableItem[]>([]);
  const [assignments, setAssignments] = useState<Record<string, 1 | 2 | 3 | 4 | null>>({});
  const canvasRef = useRef<HTMLDivElement | null>(null);

  const getBounds = () => {
    const rect = canvasRef.current?.getBoundingClientRect();
    return {
      width: rect?.width ?? 600,
      height: rect?.height ?? 520,
    };
  };

  const clamp = (val: number, min: number, max: number) => Math.min(Math.max(val, min), max);

  const randomPos = (size: number, axisMax: number, centerBias = 0.4) => {
    const usable = Math.max(axisMax - size - 20, 0);
    const center = usable / 2;
    const spread = usable * centerBias;
    const val = center + (Math.random() - 0.5) * spread * 2;
    return clamp(val, 10, usable + 10);
  };

  const buildScenario = () => {
    const { width, height } = getBounds();

    const poolByGroup: Record<1 | 2 | 3 | 4, Macro[]> = { 1: [], 2: [], 3: [], 4: [] };
    macroPool.forEach((m) => poolByGroup[m.group].push(m));

    const pickOneEach: Macro[] = [1, 2, 3, 4].flatMap((g) =>
      poolByGroup[g as 1 | 2 | 3 | 4].length ? [poolByGroup[g as 1 | 2 | 3 | 4][0]] : [],
    );

    const shuffled = [...macroPool].sort(() => Math.random() - 0.5);
    const target = Math.max(8, Math.min(14, 8 + Math.floor(Math.random() * 7)));
    const selected: Macro[] = [...pickOneEach];
    shuffled.forEach((m) => {
      if (selected.find((s) => s.id === m.id)) return;
      if (selected.length < target) selected.push(m);
    });

    const generated: DraggableItem[] = [];
    selected.forEach((m, idx) => {
      generated.push({
        id: `macro-${m.id}`,
        x: randomPos(120, width, 0.32),
        y: randomPos(120, height, 0.32),
        z: 5 + idx,
        image: m.image,
        type: 'macro',
        macro: m,
      });
    });
    covers.forEach((c, idx) => {
      generated.push({
        id: `cover-${idx}`,
        x: randomPos(200, width, 0.42),
        y: randomPos(200, height, 0.42),
        z: 50 + idx,
        image: c,
        type: 'cover',
      });
    });

    const initialAssignments: Record<string, 1 | 2 | 3 | 4 | null> = {};
    selected.forEach((m) => {
      initialAssignments[`macro-${m.id}`] = null;
    });

    return { selected, generated, initialAssignments };
  };

  useEffect(() => {
    const scenario = buildScenario();
    setActiveMacros(scenario.selected);
    setItems(scenario.generated);
    setAssignments(scenario.initialAssignments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const counts = useMemo(() => {
    const acc: Record<1 | 2 | 3 | 4, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
    Object.entries(assignments).forEach(([id, group]) => {
      if (!group) return;
      const macro = items.find((i) => i.id === id)?.macro;
      if (macro) {
        acc[group] += 1;
      }
    });
    return acc;
  }, [assignments, items]);

  const totalTypes = counts[1] + counts[2] + counts[3] + counts[4];
  const totalValue =
    counts[1] * groupWeights[1] +
    counts[2] * groupWeights[2] +
    counts[3] * groupWeights[3] +
    counts[4] * groupWeights[4];
  const indexScore = totalTypes > 0 ? totalValue / totalTypes : 0;

  const solutionCounts = useMemo(() => {
    const acc: Record<1 | 2 | 3 | 4, number> = { 1: 0, 2: 0, 3: 0, 4: 0 };
    activeMacros.forEach((m) => {
      acc[m.group] += 1;
    });
    return acc;
  }, [activeMacros]);
  const solutionTotalTypes =
    solutionCounts[1] + solutionCounts[2] + solutionCounts[3] + solutionCounts[4];
  const solutionTotalValue =
    solutionCounts[1] * groupWeights[1] +
    solutionCounts[2] * groupWeights[2] +
    solutionCounts[3] * groupWeights[3] +
    solutionCounts[4] * groupWeights[4];
  const solutionIndex = solutionTotalTypes > 0 ? solutionTotalValue / solutionTotalTypes : 0;
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta, over } = event;
    const { width, height } = getBounds();
    setItems((prev) => {
      return prev.map((it) => {
        if (it.id !== active.id) return it;
        const size = it.type === 'macro' ? 120 : 200;
        const maxX = Math.max(width - size, 0);
        const maxY = Math.max(height - size, 0);
        const nextX = clamp(it.x + delta.x, 0, maxX);
        const nextY = clamp(it.y + delta.y, 0, maxY);
        return { ...it, x: nextX, y: nextY };
      });
    });
    if (!assignments.hasOwnProperty(active.id.toString())) return;
    if (over && over.id.toString().startsWith('group-')) {
      const groupId = Number(over.id.toString().replace('group-', '')) as 1 | 2 | 3 | 4;
      setAssignments((prev) => ({ ...prev, [active.id]: groupId }));
    } else if (over && over.id === 'canvas') {
      setAssignments((prev) => ({ ...prev, [active.id]: null }));
    }
  };

  const reset = () => {
    const scenario = buildScenario();
    setActiveMacros(scenario.selected);
    setItems(scenario.generated);
    setAssignments(scenario.initialAssignments);
    setInputs({
      g1a: '',
      g1b: '',
      g2a: '',
      g2b: '',
      g3a: '',
      g3b: '',
      g4a: '',
      g4b: '',
      totalA: '',
      totalB: '',
      index: '',
    });
    setStatuses({
      g1a: null,
      g1b: null,
      g2a: null,
      g2b: null,
      g3a: null,
      g3b: null,
      g4a: null,
      g4b: null,
      totalA: null,
      totalB: null,
      index: null,
    });
  };

  const [inputs, setInputs] = useState({
    g1a: '',
    g1b: '',
    g2a: '',
    g2b: '',
    g3a: '',
    g3b: '',
    g4a: '',
    g4b: '',
    totalA: '',
    totalB: '',
    index: '',
  });

  const [statuses, setStatuses] = useState<Record<string, 'correct' | 'wrong' | null>>({
    g1a: null,
    g1b: null,
    g2a: null,
    g2b: null,
    g3a: null,
    g3b: null,
    g4a: null,
    g4b: null,
    totalA: null,
    totalB: null,
    index: null,
  });

  const enteredSumB = useMemo(
    () =>
      ['g1b', 'g2b', 'g3b', 'g4b'].reduce((acc, k) => {
        const n = Number(inputs[k as keyof typeof inputs]);
        return acc + (Number.isNaN(n) ? 0 : n);
      }, 0),
    [inputs],
  );

  const enteredSumA = useMemo(
    () =>
      ['g1a', 'g2a', 'g3a', 'g4a'].reduce((acc, k) => {
        const n = Number(inputs[k as keyof typeof inputs]);
        return acc + (Number.isNaN(n) ? 0 : n);
      }, 0),
    [inputs],
  );

  const enteredIndex = enteredSumA > 0 ? enteredSumB / enteredSumA : 0;

  const checkValue = (key: string, ...expected: number[]) => {
    const val = Number(inputs[key as keyof typeof inputs]);
    const tolerance = key === 'index' ? 0.05 : key === 'totalB' ? 0.05 : 0.001;
    const okCandidate = expected.some(
      (target) => !Number.isNaN(val) && Math.abs(val - target) <= tolerance,
    );
    setStatuses((prev) => ({ ...prev, [key]: okCandidate ? 'correct' : 'wrong' }));
  };

  const statusMark = (key: string) =>
    statuses[key] === 'correct' ? '✅' : statuses[key] === 'wrong' ? '❌' : '';

  const onInputChange = (key: keyof typeof inputs, value: string) => {
    setInputs((prev) => ({ ...prev, [key]: value }));
  };


  return (
    <div className={styles.page}>
      <TopNavBar />
      <DndContext onDragEnd={handleDragEnd}>
        <div className={styles.content}>
          <div className={styles.hero}>
            <h1 className={styles.title}>Determining Stream Health with a Biotic Index</h1>
            <p className={styles.subtitle}>
              Drag leaves and rocks off the kicknet, sort macroinvertebrates by tolerance group, and
              calculate the biotic index of your virtual stream sample.
            </p>
            <div className={styles.photos}>
              {[kick1, kick2, kick3, kick4].map((src, idx) => (
                <img key={idx} src={src} alt="Kicknet field work" />
              ))}
            </div>
          </div>

          <div className={styles.keyCard}>
            <div className={styles.keyList}>
              <div className={styles.keyRow}>
                <div className={styles.keyLabel} style={{ color: groupColors[1] }}>
                  Group 1 — Highly Sensitive to Pollution
                </div>
                <div className={styles.keyIcons}>
                  <div className={styles.keyIconItem}>
                    <img src={tinystonefly} alt="Stonefly" />
                    <span>Stonefly</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinydobsonfly} alt="Dobsonfly" />
                    <span>Dobsonfly</span>
                  </div>
                </div>
              </div>
              <div className={styles.keyRow}>
                <div className={styles.keyLabel} style={{ color: groupColors[2] }}>
                  Group 2 — Semi-Sensitive to Pollution
                </div>
                <div className={styles.keyIcons}>
                  <div className={styles.keyIconItem}>
                    <img src={tinycaddisfly} alt="Caddisfly" />
                    <span>Caddisfly</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinydragonfly} alt="Dragonfly" />
                    <span>Dragonfly</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinywaterpenny} alt="Waterpenny" />
                    <span>Waterpenny</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinycrawfish} alt="Crawfish" />
                    <span>Crawfish</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinycranefly} alt="Cranefly" />
                    <span>Cranefly</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinydamselfly} alt="Damselfly" />
                    <span>Damselfly</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinymayfly} alt="Mayfly" />
                    <span>Mayfly</span>
                  </div>
                </div>
              </div>
              <div className={styles.keyRow}>
                <div className={styles.keyLabel} style={{ color: groupColors[3] }}>
                  Group 3 — Semi-Tolerant of Pollution
                </div>
                <div className={styles.keyIcons}>
                  <div className={styles.keyIconItem}>
                    <img src={tinyorbsnail} alt="Orb Snail" />
                    <span>Orb Snail</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinyscud} alt="Scud" />
                    <span>Scud</span>
                  </div>
                </div>
              </div>
              <div className={styles.keyRow}>
                <div className={styles.keyLabel} style={{ color: groupColors[4] }}>
                  Group 4 — Tolerant of Pollution
                </div>
                <div className={styles.keyIcons}>
                  <div className={styles.keyIconItem}>
                    <img src={tinysowbug} alt="Sowbug" />
                    <span>Sowbug</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinyleech} alt="Leech" />
                    <span>Leech</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinyworm} alt="Tubifex worm" />
                    <span>Tubifex worm</span>
                  </div>
                  <div className={styles.keyIconItem}>
                    <img src={tinypouchsnail} alt="Pouch Snail" />
                    <span>Pouch Snail</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.instructions}>
            <p>
              You dredged a patch of streambed with a kicknet. Drag leaves and rocks aside to reveal
              macroinvertebrates. Then drag each macro to its pollution-tolerance group. The biotic index
              updates from your counts of types (not individuals).
            </p>
          </div>

          <div className={styles.workspace}>
            <div className={styles.leftCol}>
              <div className={styles.canvasCard}>
                <DroppableArea id="canvas" className={styles.canvas} forwardRef={canvasRef} />
                {items.map((it) => (
                  <DraggableSprite key={it.id} item={it} />
                ))}
              </div>
            </div>
            <div className={styles.panel}>
              <div className={styles.formCol}>
                {[1, 2, 3, 4].map((g) => {
                  const groupId = g as 1 | 2 | 3 | 4;
                  const keyA = `g${g}a` as keyof typeof inputs;
                  const keyB = `g${g}b` as keyof typeof inputs;
                  return (
                    <div className={styles.groupBlock} key={g}>
                      <p className={styles.groupTitle}>Group {g}:</p>
                      <div className={styles.formRow}>
                        <span>Types = (a)</span>
                        <input
                          className={styles.readonly}
                          value={inputs[keyA]}
                          onChange={(e) => onInputChange(keyA, e.target.value)}
                        />
                        <button type="button" onClick={() => checkValue(keyA, solutionCounts[groupId])}>
                          submit
                        </button>
                        <span>{statusMark(keyA)}</span>
                        <span>x {groupWeights[groupId]} = (b)</span>
                        <input
                          className={styles.readonly}
                          value={inputs[keyB]}
                          onChange={(e) => onInputChange(keyB, e.target.value)}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            checkValue(keyB, solutionCounts[groupId] * groupWeights[groupId])
                          }
                        >
                          submit
                        </button>
                        <span>{statusMark(keyB)}</span>
                      </div>
                      {/* <DroppableArea
                        id={`group-${g}`}
                        className={styles.bin}
                        style={{ borderColor: groupColors[groupId], color: groupColors[groupId] }}
                      >
                        <div className={styles.binItems}>{renderBinItems(groupId)}</div>
                      </DroppableArea> */}
                    </div>
                  );
                })}
                <div className={styles.totalsRow}>
                  <span className={styles.totalsLabel}>Total Types (sum of a) =</span>
                  <input
                    className={styles.readonly}
                    value={inputs.totalA}
                    onChange={(e) => onInputChange('totalA', e.target.value)}
                  />
                  <button type="button" onClick={() => checkValue('totalA', solutionTotalTypes)}>
                    submit
                  </button>
                  <span>{statusMark('totalA')}</span>
                  {/* <span className={styles.hint}>expected: {solutionTotalTypes}</span> */}
                </div>
                <div className={styles.totalsRow}>
                  <span className={styles.totalsLabel}>Total Value (sum of b) =</span>
                  <input
                    className={styles.readonly}
                    value={inputs.totalB}
                    onChange={(e) => onInputChange('totalB', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => checkValue('totalB', solutionTotalValue, enteredSumB)}
                  >
                    submit
                  </button>
                  <span>{statusMark('totalB')}</span>
                  {/* <span className={styles.hint}>expected: {solutionTotalValue.toFixed(1)}</span> */}
                </div>
                <div className={styles.totalsRow}>
                  <span className={styles.totalsLabel}>Total Value (b) ÷ Total Types (a) =</span>
                  <input
                    className={styles.readonly}
                    value={inputs.index}
                    onChange={(e) => onInputChange('index', e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => checkValue('index', solutionIndex, enteredIndex)}
                  >
                    submit
                  </button>
                  <span>{statusMark('index')}</span>
                  {/* <span className={styles.hint}>expected: {solutionIndex.toFixed(2)}</span> */}
                </div>
              </div>
              <div className={styles.sideCol}>
                <div className={styles.indexHeader}>
                  <p className={styles.indexTitle}>
                    This is the Index Score,
                    <br />
                    which is a wholistic measure
                    <br />
                    of the overall health of the stream.
                  </p>
                  {statuses.index === 'correct' && (
                    <div className={styles.indexChip}>Biotic Index: {solutionIndex.toFixed(2)}</div>
                  )}
                </div>
                <div className={styles.scoreValue}>{indexScore ? indexScore.toFixed(2) : ''}</div>
                <div className={styles.legendBox}>
                  <p className={styles.legendTitle}>How healthy is the stream?</p>
                  <p>Excellent .......... 3.6+</p>
                  <p>Good ................. 2.6 - 3.5</p>
                  <p>Fair ................... 2.1 - 2.5</p>
                  <p>Poor .................. 1.0 - 2.0</p>
                </div>
                <button type="button" className={styles.button} onClick={reset}>
                  Assess Another Stream
                </button>
              </div>
            </div>
          </div>
        </div>
      </DndContext>
    </div>
  );
};

export default BioticIndex;
